import { Formik } from 'formik';
import { connect } from 'react-redux';
import { object, string } from 'yup';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { bodyWithLockVersion } from '../../../app/form/helper';
import { createStory, updateStory } from '../../actions';
import Form from './form';
import Modal from '../../../app/modal/v3';
import { Box } from 'rebass';
import useToggle from 'app/hooks/toggle';

// With no `storyId`, this creates a new story instead of updating an existing
// one — used by the "Split Story from here" flow (see story-media/split-story)
// to give the member a title/dates for the story their selection is moving
// into. `onCreate` receives the new story's id once it exists; the caller owns
// whatever happens next (e.g. moving pages into it), and this form stays open
// (Formik's isSubmitting keeps Save disabled) until that resolves.
const StoryForm = ({ storyId, story, journalId, revalidateStory, onClose, onCreate, ...props }) => {

  const [tagsToggled, toggle] = useToggle();
  const [createError, setCreateError] = useState(null);
  const isCreating = !storyId;

  return (
    <Modal aboveTooltip
      title="Story Settings"
      closeOnOutsideClick={false} close={{ onClose }} isScrollActive closeButton shadeProps={{ pt: [0, 4] }}>
      <Box px={[3, 5]} mb={[7, 0]}>
        <Formik
          initialValues={{
            'began-on': story?.['began-on'] || '',
            'ended-on': story?.['ended-on'] || '',
            title: story?.title === 'Untitled Story' ? '' : story?.title,
            id: storyId || '',
            'journal-id': story?.['journal-id'] || story?.['journal-ids']?.[0] || journalId || '',
          }}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            if (isCreating) {
              setCreateError(null);
              try {
                const result = await props.createStory({
                  title: values.title,
                  'began-on': values['began-on'],
                  'ended-on': values['ended-on'],
                  'journal-id': values['journal-id'],
                });
                const [newStoryId] = result?.payload?.data?.result?.stories || [];
                await onCreate(newStoryId);
                onClose();
              } catch (err) {
                setCreateError(err);
              } finally {
                setSubmitting(false);
              }
              return;
            }

            props.updateStory(storyId, bodyWithLockVersion(story, values)).then(() => {
              revalidateStory();
              onClose();
            });
          }}
          validationSchema={object().shape({
            title: string().required(),
          })}
        >
          {formProps => {
            return (
              <Form
                hasTags={!isCreating && !!(story?.['journal-id'] || story?.['journal-ids']?.[0])}
                showCoverPhoto={!isCreating}
                error={createError}
                isShowingTags={tagsToggled}
                toggleTags={toggle}
                handleCancel={onClose}
                {...formProps}
              />
            )
          }}
        </Formik>
      </Box>
    </Modal>
  );
};

StoryForm.propTypes = {
  createStory: PropTypes.func.isRequired,
  updateStory: PropTypes.func.isRequired,
  storyId: PropTypes.string,
  journalId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func,
};

StoryForm.defaultProps = {
  storyId: null,
  journalId: null,
  onCreate: () => {},
};

export default connect(null, {
  createStory,
  updateStory,
})(StoryForm);
